import React from 'react';
import { Modal, Button, Progress, Statistic } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setIsTariffInfoModalOpen,
  setTariffInfoModalRoom,
} from 'store/slices/windowStateSlice';
import colors from 'scss/variables/colors.module.scss';
import analytics from 'api/analytics';
import { IMainInfo, mainInfoDefault } from 'interfaces/AnalyticTypes';
import { Tariffs } from 'components/TariffsComponent/tariffsData';
import { getPeriod, PeriodTypes } from 'helpers/getPeriod';
import { runExtendTariff } from 'helpers/tariffPayment';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import PlanDetailsCard from 'components/PlanDetailsCard/PlanDetailsCard';
import RowLoader from 'components/Sceletons/RowLoader';
import classes from './TariffInfoModal.module.scss';
import { getTheme } from 'helpers/getTheme';
import classNames from 'classnames';

const TariffInfoModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isTariffInfoModalOpen, tariffInfoModalRoom, messageApi } =
    useAppSelector((state) => state.windowState);
  const { userData } = useAppSelector((state) => state.userData);

  const [isExtending, setIsExtending] = React.useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = React.useState(false);
  const [usage, setUsage] = React.useState<IMainInfo>(mainInfoDefault);

  const isTrial = tariffInfoModalRoom?.tariff?.tariff_name === Tariffs.Trial;
  const canExtend =
    !isTrial && Boolean(tariffInfoModalRoom?.tariff?.last_payment_intent?.link);

  const membersUsed =
    (usage?.max_members_amount || 0) - (usage?.members_amount_left || 0);

  const handleClose = () => {
    dispatch(setIsTariffInfoModalOpen(false));
    dispatch(setTariffInfoModalRoom(null));
  };

  const handleExtend = async () => {
    if (!tariffInfoModalRoom) return;
    setIsExtending(true);
    await runExtendTariff(tariffInfoModalRoom, messageApi, t);
    setIsExtending(false);
  };

  const handleChangeTariff = () => {
    if (tariffInfoModalRoom?.id) {
      localStorage.setItem('ended_tariff_lifetime', tariffInfoModalRoom.id);
    }
    handleClose();
    navigate('/tariffs');
  };

  const loadUsage = async (roomId: string) => {
    setIsLoadingUsage(true);
    try {
      const period = getPeriod(PeriodTypes.week);
      const { data } = await analytics.getAnalyticsMainInfo({
        room: roomId,
        from: period[0],
        to: period[1],
      });
      setUsage(data);
    } catch (e) {
      messageApi?.error(t('Analytics.errors.main_info'));
    } finally {
      setIsLoadingUsage(false);
    }
  };

  React.useEffect(() => {
    if (isTariffInfoModalOpen && tariffInfoModalRoom?.id) {
      loadUsage(tariffInfoModalRoom.id);
    } else {
      setUsage(mainInfoDefault);
    }
  }, [isTariffInfoModalOpen, tariffInfoModalRoom?.id]);

  const tariffEndsAt = tariffInfoModalRoom?.tariff?.tariff_ends_at
    ? getDateWithTimeZone(
        String(dayjs(tariffInfoModalRoom.tariff.tariff_ends_at)),
        userData?.date_format || 'international'
      )
    : null;

  return (
    <Modal
      open={isTariffInfoModalOpen}
      onCancel={handleClose}
      width={560}
      title={t('TariffInfoModal.title')}
      footer={null}
      destroyOnHidden
      className={classNames(classes.modal, getTheme())}
    >
      {tariffInfoModalRoom && (
        <div className={classes.body}>
          <div className={classes.roomName}>{tariffInfoModalRoom.name}</div>

          <PlanDetailsCard room={tariffInfoModalRoom} />

          {tariffEndsAt && (
            <div className={classes.endsAt}>
              <span className={classes.endsAtLabel}>
                {t('TariffInfoModal.tariffEndsAt')}
              </span>
              <span className={classes.endsAtValue}>{tariffEndsAt}</span>
            </div>
          )}

          <div className={classes.usageBlock}>
            <div className={classes.usageTitle}>
              {t('TariffInfoModal.usage')}
            </div>
            <div className={classes.usageGrid}>
              <div className={classes.usageItem}>
                <div className={classes.usageLabel}>
                  {t('TariffInfoModal.memory')}
                </div>
                <Progress
                  type='circle'
                  strokeColor={colors.primary100}
                  size={110}
                  percent={
                    usage?.max_space
                      ? Number(
                          (
                            (usage.total_document_size / usage.max_space) *
                            100
                          ).toFixed(1)
                        )
                      : 0
                  }
                  format={(percent) =>
                    !isLoadingUsage ? (
                      <div className={classes.usageCircleText}>
                        <div className={classes.usageValue}>
                          {usage?.total_document_size}/
                          {usage?.max_space}
                          {t('Tariffs.gb')}
                        </div>
                        <div className={classes.usagePercent}>{percent}%</div>
                      </div>
                    ) : (
                      <RowLoader height={22} width={72} padding='0' />
                    )
                  }
                />
              </div>

              <div className={classes.usageItem}>
                <div className={classes.usageLabel}>
                  {t('TariffInfoModal.users')}
                </div>
                <Progress
                  type='circle'
                  strokeColor={colors.primary100}
                  size={110}
                  percent={
                    usage?.max_members_amount
                      ? Number(
                          (
                            (membersUsed / usage.max_members_amount) *
                            100
                          )
                        )
                      : 0
                  }
                  format={(percent) =>
                    !isLoadingUsage ? (
                      <div className={classes.usageCircleText}>
                        <div className={classes.usageValue}>
                          {membersUsed}/{usage?.max_members_amount}
                        </div>
                        <div className={classes.usagePercent}>{percent}%</div>
                      </div>
                    ) : (
                      <RowLoader height={22} width={72} padding='0' />
                    )
                  }
                />
              </div>

              <div className={classes.usageItem}>
                <div className={classes.usageLabel}>
                  {t('TariffInfoModal.files')}
                </div>
                {isLoadingUsage ? (
                  <RowLoader height={40} width={100} padding='10px 0 0 0' />
                ) : (
                  <Statistic
                    value={usage?.total_document_count}
                    groupSeparator=' '
                  />
                )}
              </div>
            </div>
          </div>

          <div className={classes.footer}>
            {canExtend && (
              <Button
                type='primary'
                loading={isExtending}
                onClick={handleExtend}
              >
                {t('TariffInfoModal.extend')}
              </Button>
            )}
            <Button onClick={handleChangeTariff}>
              {t('TariffInfoModal.change')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TariffInfoModal;
